
import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Dialog } from '@capacitor/dialog';
import { MaskitoDirective } from '@maskito/angular';
import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { TranslateService } from '@ngx-translate/core';
import { LoadingButtonComponent } from '@scandium-oy/ngx-scandium';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { take } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { DashboardViewComponent } from '../components/views/dashboard/view-dashboard.component';
import { Timesheet } from '../models/timesheet.model';
import { SignatureService } from '../services/signature.service';
import { CheckTimesheetsComponent } from '../timesheets/process/component/check-timesheets.component';

@Component({
  selector: 'app-signature-page',
  templateUrl: './signature.page.html',
  styleUrls: ['./signature.page.scss'],
  providers: [SignatureService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    DashboardViewComponent,
    FormsModule,
    LoadingButtonComponent,
    MaskitoDirective,
    NgxExtendedPdfViewerModule,
    CheckTimesheetsComponent,
  ],
})
export class SignaturePage {
  private route = inject(ActivatedRoute);
  private signatureService = inject(SignatureService);
  private translate = inject(TranslateService);

  private item = '';
  url = signal<string>(null);
  timesheets = signal<Timesheet[]>([]);

  readonly passcodeMask: MaskitoOptions = {
    mask: [/\w/, ' ', /\w/, ' ', /\w/, ' ', /\w/, ' ', /\w/, ' ', /\w/],
  };

  readonly maskPredicate: MaskitoElementPredicate = async (el) => (el as unknown as HTMLIonInputElement).getInputElement();

  hasPasscode = signal(false);
  passcode = signal<string>(null);
  loading = signal(false);
  error = signal(false);
  expired = signal(false);
  declined = signal(false);
  alreadySigned = signal(false);
  type = signal<'offer' | 'timesheet' | 'site-offer'>(null);

  signed = signal<{ ipAddress: string; email: string }>(null);

  enterPasscode() {
    this.loading.set(true);
    this.error.set(false);
    const item = this.route.snapshot.paramMap.get('id');
    this.signatureService.passcode(item, this.passcode().replace(/\s/g, '')).pipe(
      take(1),
    ).subscribe({
      next: (ret) => {
        if (ret.status === 'invalid') {
          this.error.set(true);
        } else if (ret.status === 'expired') {
          this.expired.set(true);
        } else if (ret.status === 'signed') {
          this.alreadySigned.set(true);
        } else {
          this.type.set(ret.type);
          this.hasPasscode.set(true);
          if (ret.type === 'offer') {
            this.item = ret.item;
            this.url.set(ret.url);
          } else if (ret.type === 'timesheet') {
            this.item = ret.item;
            this.timesheets.set(ret.items ?? []);
          } else if (ret.type === 'site-offer') {
            this.item = ret.item;
            this.url.set(ret.url);
          }
        }
      },
      complete: () => this.loading.set(false),
      error: () => this.loading.set(false),
    });
  }

  sign() {
    Dialog.confirm({
      message: this.translate.instant('signature.confirmSignature'),
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
      if (dialog.value) {
        this.loading.set(true);
        const guid = this.route.snapshot.paramMap.get('id');
        this.signatureService.sign(guid, this.item).pipe(
          take(1),
        ).subscribe({
          next: (ret) => {
            this.signed.set({ ipAddress: ret.ipAddress, email: ret.email });
          },
          complete: () => this.loading.set(false),
          error: () => this.loading.set(false),
        });
      }
    });
  }

  decline() {
    Dialog.confirm({
      message: this.translate.instant('signature.confirmDecline'),
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
      if (dialog.value) {
        this.loading.set(true);
        const guid = this.route.snapshot.paramMap.get('id');
        this.signatureService.decline(guid, this.item).pipe(
          take(1),
        ).subscribe({
          next: (ret) => {
            this.signed.set({ ipAddress: ret.ipAddress, email: ret.email });
            this.declined.set(true);
          },
          complete: () => this.loading.set(false),
          error: () => this.loading.set(false),
        });
      }
    });
  }

  download() {
    Browser.open({ url: this.url() });
  }
}
